import { useState } from 'react'
import { Users, Plus, Minus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { IngredientScaler } from '@/services/ingredients'

interface IngredientScalerProps {
  ingredients: string[]
  originalServings: number
  className?: string
  showCard?: boolean
  onServingsChange?: (servings: number, scaledIngredients: string[]) => void
}

export function IngredientScalerComponent({ 
  ingredients, 
  originalServings, 
  className = '',
  showCard = true,
  onServingsChange,
}: IngredientScalerProps) {
  const [currentServings, setCurrentServings] = useState(originalServings || 4)
  const [scaledIngredients, setScaledIngredients] = useState(ingredients)

  const handleServingsChange = (newServings: number) => {
    if (newServings < 1 || newServings > 50) return
    
    setCurrentServings(newServings)
    const scaled = IngredientScaler.scaleIngredients(
      ingredients, 
      originalServings || 4, 
      newServings
    )
    setScaledIngredients(scaled)
    onServingsChange?.(newServings, scaled)
  }

  const adjustServings = (delta: number) => {
    handleServingsChange(currentServings + delta)
  }

  const scalingSuggestions = IngredientScaler.getScalingSuggestions(originalServings || 4)

  const content = (
    <div className="space-y-4">
      {/* Serving Size Controls */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <Users className="h-4 w-4" />
            Aantal porties
          </label>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => adjustServings(-1)}
              disabled={currentServings <= 1}
              className="h-8 w-8 p-0"
            >
              <Minus className="h-3 w-3" />
            </Button>
            <Input
              type="number"
              value={currentServings}
              onChange={(e) => handleServingsChange(parseInt(e.target.value) || 1)}
              min="1"
              max="50"
              className="w-16 text-center h-8"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => adjustServings(1)}
              disabled={currentServings >= 50}
              className="h-8 w-8 p-0"
            >
              <Plus className="h-3 w-3" />
            </Button>
          </div>
        </div>

        {/* Quick Scale Buttons */}
        <div className="flex flex-wrap gap-1">
          {scalingSuggestions.slice(0, 6).map(servings => (
            <Button
              key={servings}
              variant={currentServings === servings ? "primary" : "outline"}
              size="sm"
              onClick={() => handleServingsChange(servings)}
              className="h-7 px-2 text-xs"
            >
              {servings}
            </Button>
          ))}
        </div>

        {currentServings !== (originalServings || 4) && (
          <div className="text-xs text-gray-500">
            Ingrediënten aangepast van {originalServings || 4} naar {currentServings} porties
          </div>
        )}
      </div>

      {/* Scaled Ingredients List */}
      <div className="space-y-2">
        <h4 className="font-medium text-gray-900">Ingrediënten</h4>
        <ul className="space-y-2">
          {scaledIngredients.map((ingredient, index) => (
            <li key={index} className="flex items-start gap-2 text-sm">
              <span className="w-2 h-2 bg-orange-500 rounded-full mt-1.5 flex-shrink-0"></span>
              <span className={
                ingredient !== ingredients[index] 
                  ? 'font-medium text-orange-700' 
                  : 'text-gray-700'
              } itemProp="recipeIngredient">
                {ingredient}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )

  if (showCard) {
    return (
      <Card className={className}>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Ingrediënten</CardTitle>
        </CardHeader>
        <CardContent>
          {content}
        </CardContent>
      </Card>
    )
  }

  return (
    <div className={className}>
      {content}
    </div>
  )
}

export default IngredientScalerComponent
